import { createScaffolderFieldExtension, FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import type { FieldValidation } from '@rjsf/utils';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';

interface Template {
  name: string;
  description: string;
  type: string;
  versions: string[];
  current_version: string;
}

interface TemplateSelectorValue {
  template: string;
  version: string;
  branch: string;
}

interface TemplateSelectorFieldProps extends FieldExtensionComponentProps<TemplateSelectorValue> {
  template_type?: string;
}

export const TemplateSelectorFieldIaC = (props: TemplateSelectorFieldProps) => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [environment, setEnvironment] = useState('');
  const [branch, setBranch] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentValue = props.formData || { template: '', version: '', branch: '' };
  const selectedTemplate = templates.find(t => t.name === currentValue.template);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.template_type, branch]);

  useEffect(() => {
    if (!branch) return;
    props.onChange({ template: '', version: '', branch: branch });
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const fetchBranches = async () => {
    try {
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/devops-api-proxy/branches`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const branchesData = await response.json();
      setBranches(branchesData);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = (await identityApi.getCredentials()).token;
      const typeFilter: string =
        (props.uiSchema?.['ui:options']?.template_type as string) ||
        props.template_type ||
        'iac';
      const url = `${backendUrl}/api/devops-api-proxy/templates?templateBranch=${encodeURIComponent(branch)}`;

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const templatesData = await response.json();
      const filteredTemplates = (templatesData.apiPayload || []).filter((tpl: any) => tpl.type === typeFilter);
      setTemplates(filteredTemplates);

      if (!branch || branch === '') {
        setBranch(templatesData.defaultBranch);
        return;
      }
      setEnvironment(templatesData.environment);

      if (templatesData.environment === 'preproduction') {
        await fetchBranches();
      }
    } catch (err) {
      console.error('❌ Error fetching IaC templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateName: string) => {
    const template = templates.find(t => t.name === templateName);
    props.onChange({
      branch: branch,
      template: templateName,
      version: template?.current_version || '',
    });
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gridGap={2} p={2}>
        <CircularProgress size={20} />
        <Typography>Loading templates...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" style={{ marginBottom: 16 }}>
          Error loading templates: {error}
        </Alert>
        <button
          onClick={fetchTemplates}
          style={{ padding: '8px 16px', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
        >
          🔄 Retry
        </button>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        🏗️ IaC Template Configuration
      </Typography>

      <Box display="flex" gridGap={2} flexDirection="column">
        {environment === 'preproduction' && (
          <FormControl fullWidth variant="standard">
            <InputLabel>Branch</InputLabel>
            <Select
              value={currentValue.branch}
              onChange={(e) => setBranch(e.target.value as string)}
              label="Branch"
            >
              {branches.map(b => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth variant="standard">
          <InputLabel>IaC Template</InputLabel>
          <Select
            value={currentValue.template}
            onChange={(e) => handleTemplateChange(e.target.value as string)}
            label="IaC Template"
          >
            <MenuItem value="">
              <em>Select a template</em>
            </MenuItem>
            {templates.map((template) => (
              <MenuItem key={template.name} value={template.name}>
                <Box>
                  <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                    {template.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {template.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTemplate && (
          <Box
            mt={2}
            p={2}
            style={{ borderRadius: '4px', border: '1px solid #3e404bff' }}
          >
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
              📋 Selected Template Details
            </Typography>
            <Typography variant="body2">
              <strong>Name:</strong> {selectedTemplate.name}
            </Typography>
            <Typography variant="body2">
              <strong>Description:</strong> {selectedTemplate.description}
            </Typography>
            <Typography variant="body2">
              <strong>Type:</strong> {selectedTemplate.type}
            </Typography>
            <Typography variant="body2">
              <strong>Current Version:</strong> {selectedTemplate.current_version}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const templateSelectorIaCValidation = (
  value: TemplateSelectorValue,
  validation: FieldValidation,
) => {
  if (!value) {
    validation.addError('This field is required.');
  }
};

export const TemplateSelectorCaseFieldExtensionIaC = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'TemplateSelectorFieldIaC',
    component: TemplateSelectorFieldIaC,
    validation: templateSelectorIaCValidation,
  }),
);
